import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/* The search bar at the top searches the page you're on (Duncan 2026-10-17: "only search in
   filter by the page that you're currently on" — every page; on a project, its open tab). A page
   (or tab) calls usePageSearch('Search job costs…') and filters its own list by the text it gets
   back; the bar (AppHeader, or ProjectHeader on a project) shows that page's placeholder and holds
   the text. The text clears when you go to another page, tab or section. A page that doesn't call
   it gets no search box. */
const PageSearchContext = createContext(null);

export function PageSearchProvider({ children }) {
  const [query, setQuery] = useState('');
  const [owner, setOwner] = useState(null); // { id, placeholder } of the page on screen
  const { pathname } = useLocation();
  // A new page, or another tab or section taking the box over (AR's views and Settings'
  // sections switch without a route change), starts empty.
  const ownerId = owner ? owner.id : null;
  useEffect(() => { setQuery(''); }, [pathname, ownerId]);
  const value = useMemo(() => ({ query, setQuery, owner, setOwner }), [query, owner]);
  return <PageSearchContext.Provider value={value}>{children}</PageSearchContext.Provider>;
}

let nextId = 1;

/** [text, setText] for a page or tab: shows the bar with `placeholder` while it's mounted. For a
 *  page that already has a search box of its own, so that box and the bar stay the same. A null
 *  placeholder keeps the text but shows no box (a view of the page with nothing to search). */
export function usePageSearchState(placeholder) {
  const ctx = useContext(PageSearchContext);
  const id = useRef(0);
  if (!id.current) id.current = nextId++;
  const setOwner = ctx ? ctx.setOwner : null;
  useEffect(() => {
    if (!setOwner) return undefined;
    const me = id.current;
    setOwner({ id: me, placeholder });
    return () => setOwner((o) => (o && o.id === me ? null : o));
  }, [setOwner, placeholder]);
  const [local, setLocal] = useState(''); // no provider (a test render): a box of its own
  return ctx ? [ctx.query, ctx.setQuery] : [local, setLocal];
}

/** The text typed in the bar, for a page or tab that filters by it (see usePageSearchState). */
export function usePageSearch(placeholder) {
  return usePageSearchState(placeholder)[0];
}

/** For the bars: { query, setQuery, owner } — owner null when the page has no search. */
export function useTopSearch() {
  return useContext(PageSearchContext);
}

/** What a list says when the search hides every row it has: { title, body } for its empty state. */
export function noMatchText(query) {
  return {
    title: 'No matches',
    body: `Nothing here matches \u201c${String(query || '').trim()}\u201d. Clear the search at the top to see everything.`,
  };
}

/** Whether a row matches the search: every word typed appears in one of its fields (any case). */
export function matchesSearch(query, ...fields) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = fields.flat().filter((f) => f !== null && f !== undefined && f !== '')
    .map((f) => String(f).toLowerCase()).join(' \u0001 ');
  return words.every((w) => text.includes(w));
}
